import { PropsWithChildren, ReactNode, useMemo, useState } from 'react';

import {
  Avatar,
  AvatarProps,
  ChakraBox,
  ChakraCollapse,
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyIconButton,
  Text,
  Tooltip,
} from '@equip.health/ui';
import { capitalizeFirstLetter } from '~/lib/common';
import { AvatarUserTypeDisplay } from '~/lib/constants/patients.constants';

const { typography } = EquipUIFireflyV2Theme;

interface CollapsibleUserCardProps extends PropsWithChildren {
  avatarProps: Pick<AvatarProps, 'name' | 'src' | 'userType'>;
  children: ReactNode;
  defaultExpanded?: boolean;
  expandIconColor?: string;
  hideTooltip?: boolean;
  id: string;
  labels?: ReactNode;
  nameProps: {
    chosenName?: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    prefix?: string;
    suffix?: string;
  };
  pronouns?: string[];
  role?: string;
  tags?: ReactNode;
}

const CollapsibleUserCard = ({
  avatarProps,
  children,
  defaultExpanded = false,
  expandIconColor = 'neutral.tertiary',
  hideTooltip = false,
  id,
  labels,
  nameProps,
  pronouns,
  role,
  tags,
}: CollapsibleUserCardProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const handleToggle = (): void => setIsExpanded(!isExpanded);

  const TOP_LEVEL_ID = `${id}__collapsible-user-card`;

  const tooltipText = useMemo(() => {
    if (hideTooltip) return null;
    return [
      // Prefix (optional)
      nameProps.prefix && `${capitalizeFirstLetter(nameProps.prefix)}.`,
      // First Name or Chosen Name (Chosen Name takes precedence)
      capitalizeFirstLetter(nameProps.chosenName || nameProps.firstName),
      // Middle Name (optional)
      nameProps.middleName && `${capitalizeFirstLetter(nameProps.middleName)}`,
      // Last Name with optional Suffix
      `${capitalizeFirstLetter(nameProps.lastName)}${nameProps.suffix ? `, ${nameProps.suffix}.` : ''}`,
      // First Name in parentheses (only if chosenName is provided)
      nameProps.chosenName && `(${nameProps.firstName})`,
      // Pronouns in parentheses (optional)
      pronouns?.length > 0 && `(${pronouns.join(', ')})`,
    ]
      .filter(Boolean)
      .join(' ');
  }, [hideTooltip, nameProps, pronouns]);

  return (
    <ChakraFlex
      borderColor="neutral.line"
      borderRadius="16px"
      borderStyle="solid"
      borderWidth="1px"
      direction="column"
      gap={2}
      id={`${TOP_LEVEL_ID}__container`}
      justifyContent="space-between"
      marginY="0px"
      padding="18px"
      width="100%"
    >
      <ChakraFlex alignItems="center" gap="12px">
        <ChakraBox flexShrink={0}>
          <Avatar
            {...avatarProps}
            id={TOP_LEVEL_ID}
            tooltipWrapper={
              !hideTooltip &&
              (({ children }) => (
                <Tooltip
                  id={`${id}__tooltip`}
                  placement="right"
                  tooltipText={tooltipText}
                  width="max-content"
                >
                  {children}
                </Tooltip>
              ))
            }
          />
        </ChakraBox>
        <ChakraFlex direction="column" flex="1" gap="2px" minWidth="0">
          <Text
            id={`${TOP_LEVEL_ID}__primary-text`}
            overflow="hidden"
            textOverflow="ellipsis"
            variant="bodyMedium"
            whiteSpace="nowrap"
          >
            {nameProps.prefix && `${capitalizeFirstLetter(nameProps.prefix)}. `}
            {capitalizeFirstLetter(nameProps.chosenName || nameProps.firstName)}
            {nameProps.middleName &&
              ` ${capitalizeFirstLetter(nameProps.middleName)}`}
            {` ${capitalizeFirstLetter(nameProps.lastName)}`}
            {nameProps.suffix && `, ${nameProps.suffix}.`}
            {nameProps.chosenName && (
              <span
                data-testid={`${TOP_LEVEL_ID}__chosen-name`}
                style={{ ...typography.caption }}
              >
                {` (${nameProps.firstName})`}
              </span>
            )}
            {pronouns?.length > 0 && (
              <span
                data-testid={`${TOP_LEVEL_ID}__pronouns`}
                style={{ ...typography.caption }}
              >
                {` (${pronouns.join(', ')})`}
              </span>
            )}
          </Text>
          <Text
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__role`}
            variant="caption"
          >
            {role ?? AvatarUserTypeDisplay[avatarProps.userType]}
          </Text>
        </ChakraFlex>
        <ChakraFlex alignItems="center" gap="18px">
          {labels}
          {tags}
          <ChakraBox
            sx={{
              '& button': { height: 'unset', minWidth: 'unset' },
              '& button:hover': { background: 'transparent' },
            }}
          >
            <FireflyIconButton
              color={expandIconColor}
              icon={
                isExpanded
                  ? FAIconName.chevronUpRegular
                  : FAIconName.chevronDownRegular
              }
              id={`${TOP_LEVEL_ID}__expand`}
              onClick={handleToggle}
              variant="tertiary"
            />
          </ChakraBox>
        </ChakraFlex>
      </ChakraFlex>
      <ChakraCollapse in={isExpanded}>{children}</ChakraCollapse>
    </ChakraFlex>
  );
};

export default CollapsibleUserCard;
